/**
 * Race difficulty: the setting a race is made with. Shared by the generator, the
 * protocol and the UI, so it is light (exported from the package root too).
 *
 *  - easy / medium / hard: every level of the race at that difficulty, varied from
 *    level to level by its main mechanic, theme and music;
 *  - rising: each level harder than the last, from the gentlest level to the hardest.
 *
 * A level's place in its series gives its intensity, 0 (gentlest) .. 1 (hardest),
 * stored as LevelData.difficulty; the generator turns the intensity into the level's
 * plan (levelgen/plan.ts): above all how many actions follow each other and how little
 * time there is between them, then speed, timing windows and mechanics.
 */
export const DIFFICULTIES = ['easy', 'medium', 'hard', 'rising'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

/** The difficulties with one intensity for every level. */
export type FixedDifficulty = Exclude<Difficulty, 'rising'>;

/** The levels of one race, as the generator sees them (see generateLevels). */
export interface LevelSeries {
  /** The race's seed. */
  seed: number;
  difficulty: Difficulty;
  /**
   * How many levels the race has: 'rising' climbs from the gentlest level to the
   * hardest across them. null = open-ended (a race against the clock): 'rising' climbs
   * over RISING_SPAN levels, then stays at the top, with variety.
   */
  count: number | null;
}

/** Intensity of every level of a fixed difficulty. */
export const DIFFICULTY_INTENSITY: Readonly<Record<FixedDifficulty, number>> = {
  easy: 0.15,
  medium: 0.5,
  hard: 1,
};

/** An open-ended rising series climbs over this many levels (like a classic 10-level race). */
export const RISING_SPAN = 10;

export function isDifficulty(value: unknown): value is Difficulty {
  return (DIFFICULTIES as readonly unknown[]).includes(value);
}

/**
 * Intensity of level `index` (0-based) of a series, i.e. its LevelData.difficulty.
 * Rising: 0 on the first level, 1 on the last one (open-ended: from level RISING_SPAN
 * on), evenly in between; a race of one level gets the gentlest one.
 */
export function levelIntensity(
  difficulty: Difficulty,
  count: number | null,
  index: number,
): number {
  if (difficulty !== 'rising') return DIFFICULTY_INTENSITY[difficulty];
  const span = count ?? RISING_SPAN;
  return span > 1 ? Math.min(1, index / (span - 1)) : 0;
}

/**
 * The fixed difficulty a level's intensity is closest to, e.g. to show how hard the
 * current level of a rising race is.
 */
export function intensityTier(intensity: number): FixedDifficulty {
  const { easy, medium, hard } = DIFFICULTY_INTENSITY;
  if (intensity < (easy + medium) / 2) return 'easy';
  return intensity < (medium + hard) / 2 ? 'medium' : 'hard';
}
